(() => {
  'use strict';

  if (window.EmporiaPdpSections) {
    window.EmporiaPdpSections.initialize(document);
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function activateTab(tab, { focus = false } = {}) {
    if (!tab) return;

    const tabsRoot = tab.closest('[data-emporia-feature-tabs]');
    if (!tabsRoot) return;

    const tabs = [...tabsRoot.querySelectorAll('[data-emporia-feature-tab]')];
    const panels = [...tabsRoot.querySelectorAll('[data-emporia-feature-panel]')];
    const panelId = tab.getAttribute('aria-controls');

    tabs.forEach((candidate) => {
      const isActive = candidate === tab;
      candidate.setAttribute('aria-selected', String(isActive));
      candidate.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach((panel) => {
      panel.hidden = panel.id !== panelId;
    });

    if (focus) tab.focus();
  }

  function initializeTabs(tabsRoot) {
    if (!tabsRoot || tabsRoot.dataset.emporiaInitialized === 'true') return;

    const tabs = [...tabsRoot.querySelectorAll('[data-emporia-feature-tab]')];
    if (!tabs.length) return;

    tabsRoot.dataset.emporiaInitialized = 'true';

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activateTab(tab));
      tab.addEventListener('keydown', (event) => {
        const currentIndex = tabs.indexOf(tab);
        let nextIndex;

        switch (event.key) {
          case 'ArrowLeft':
            nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
            break;
          case 'ArrowRight':
            nextIndex = (currentIndex + 1) % tabs.length;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = tabs.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        activateTab(tabs[nextIndex], { focus: true });
      });
    });

    const selectedTab =
      tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    activateTab(selectedTab);
  }

  function observeOnce(element, callback) {
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        callback();
      },
      { rootMargin: '200px 0px' }
    );

    observer.observe(element);
  }

  function initializeHostedVideo(video) {
    if (!video || video.dataset.emporiaVideoInitialized === 'true') return;

    video.dataset.emporiaVideoInitialized = 'true';
    const wantsAutoplay = video.hasAttribute('data-emporia-autoplay');

    if (wantsAutoplay && reducedMotion.matches) {
      video.controls = true;
    }

    observeOnce(video, () => {
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.load();

      if (wantsAutoplay && !reducedMotion.matches) {
        video.muted = true;
        video.playsInline = true;
        video.play().catch(() => {
          video.controls = true;
        });
      }
    });
  }

  function externalVideoUrl(container) {
    const provider = container.dataset.videoProvider;
    const videoId = container.dataset.videoId;
    const wantsAutoplay = container.dataset.autoplay === 'true';
    const shouldAutoplay = wantsAutoplay && !reducedMotion.matches;
    const showControls =
      container.dataset.controls === 'true' || (wantsAutoplay && reducedMotion.matches);
    const shouldLoop = container.dataset.loop === 'true';
    let url;

    if (provider === 'youtube') {
      url = new URL(`https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}`);
      url.searchParams.set('autoplay', shouldAutoplay ? '1' : '0');
      url.searchParams.set('controls', showControls ? '1' : '0');
      url.searchParams.set('playsinline', '1');
      if (shouldAutoplay) url.searchParams.set('mute', '1');
      if (shouldLoop) {
        url.searchParams.set('loop', '1');
        url.searchParams.set('playlist', videoId);
      }
    } else if (provider === 'vimeo') {
      url = new URL(`https://player.vimeo.com/video/${encodeURIComponent(videoId)}`);
      url.searchParams.set('autoplay', shouldAutoplay ? '1' : '0');
      url.searchParams.set('controls', showControls ? '1' : '0');
      url.searchParams.set('loop', shouldLoop ? '1' : '0');
      url.searchParams.set('playsinline', '1');
      if (shouldAutoplay) url.searchParams.set('muted', '1');
    }

    return url?.toString();
  }

  function initializeExternalVideo(container) {
    if (!container || container.dataset.emporiaVideoInitialized === 'true') return;

    const frame = container.querySelector('[data-emporia-video-frame]');
    if (!frame) return;

    container.dataset.emporiaVideoInitialized = 'true';
    frame.addEventListener('load', () => container.classList.add('is-loaded'));

    observeOnce(container, () => {
      const url = externalVideoUrl(container);
      if (url) frame.src = url;
    });
  }

  async function initializeRecommendations(section) {
    if (!section || section.dataset.emporiaInitialized === 'true') return;

    const url = section.dataset.recommendationsUrl;
    if (!url) return;

    section.dataset.emporiaInitialized = 'true';

    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Recommendations request failed (${response.status})`);

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = parsed.querySelector('[data-emporia-recommendations]');
      if (!replacement) throw new Error('Recommendations response was empty');

      section.innerHTML = replacement.innerHTML;
      section.dataset.emporiaLoaded = 'true';
      if (!section.firstElementChild) section.hidden = true;
    } catch (error) {
      section.hidden = true;
      console.warn('[Emporia recommendations]', error);
    }
  }

  function initialize(root) {
    if (!root?.querySelectorAll) return;

    root.querySelectorAll('[data-emporia-feature-tabs]').forEach(initializeTabs);
    root.querySelectorAll('.emporia-media-text__video').forEach(initializeHostedVideo);
    root
      .querySelectorAll('[data-emporia-external-video]')
      .forEach(initializeExternalVideo);
    root
      .querySelectorAll('[data-emporia-recommendations]')
      .forEach(initializeRecommendations);
  }

  window.EmporiaPdpSections = { initialize };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document));
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });
})();
